/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ArrowLeft } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useFetchOrganizer } from 'modules/organizer/ui/hooks/use-fetch-organizer';
import UDEmptyButton from 'modules/ud-ui/components/empty-button';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { history } from 'store/store';
import backgroundElems from 'ud-ui/components/fabric-canvas/addons/background-elems';
import useFabric from 'ud-ui/components/fabric-canvas/hooks/use-fabric';
import useReplacer from 'ud-ui/components/fabric-canvas/hooks/use-replacer';
import image from 'ud-ui/components/images/placeholders/background.png';
import replaceFakeData from '../../domain/utils/replace-fake-data';
import { selectPreviewState } from '../../store/editor/selectors';
import useBoards from '../hooks/use-boards';
import { useLoadTemplates } from '../hooks/use-load-templates';
import useTemplates from '../hooks/use-templates';

function BoardPreview() {
  const state = useSelector(selectPreviewState);
  const { canvas, manager } = useFabric('static', { data: state?.resolution });
  const replacer = useReplacer(manager);
  const { activeTemplate } = useTemplates();
  const { activeBoard, activeScheme } = useBoards();
  const [isInit, setIsInit] = useState(false);

  useLoadTemplates();
  useFetchOrganizer();

  useEffect(() => {
    if (!manager) return;
    if (!isInit) return;
    let scheme = state?.scheme;
    if (!scheme) {
      scheme = activeScheme;
    }

    if (!scheme) return;
    const paramsForBorder = { color: '#D9B244', width: 2, dashArray: true };

    manager.loadLocal({ scheme }).then(() => {
      backgroundElems.drawBorder(manager.canvas, manager.resolution.value, paramsForBorder);
      replaceFakeData({ replacer });
      backgroundElems.border?.bringToFront();
    });

    manager.canvas.setWidth(window.innerWidth);
    manager.canvas.setHeight(window.innerHeight - 88);
    const offset = { zoom: 0.999 };
    manager.normalizedViewport(offset);

    const resizing = () => {
      manager.canvas.setWidth(window.innerWidth);
      manager.canvas.setHeight(window.innerHeight - 88);
      backgroundElems.drawBorder(manager.canvas, manager.resolution.value, paramsForBorder);
      manager.normalizedViewport(offset);
    };

    window.addEventListener('resize', resizing);

    return () => {
      window.removeEventListener('resize', resizing);
    };
  }, [activeScheme, isInit, manager, replacer, state?.scheme]);

  useEffect(() => {
    if (!activeTemplate) return;
    manager?.setResolution(activeTemplate.resolution);
    setIsInit(true);
  }, [activeTemplate, manager]);

  const onBackToEditor = () => {
    if (!activeTemplate || !activeBoard) return;
    history.push(Pages.TEMPLATE.builders.editor(activeTemplate.id, activeBoard.id));
  };

  return (
    <MainBlock>
      <Header>
        <UDEmptyButton onClick={onBackToEditor} css={buttonStyle} icon={<ArrowLeft strokeWidth={1} size={20} />}>
          Назад
        </UDEmptyButton>
      </Header>
      <CanvasWrapper>{canvas}</CanvasWrapper>
    </MainBlock>
  );
}

export default BoardPreview;

const MainBlock = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
`;

const Header = styled.div`
  display: flex;
  padding: 30px;
  background-color: #1a1a29;
  height: 88px;
`;

const buttonStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  gap: 10px;
  background-color: #ffffff;
  padding: 4px 10px;
  border-radius: 6px;

  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  color: #000000;
`;

const CanvasWrapper = styled.div`
  width: 100vw;
  height: 100%;

  background: url('${image}') no-repeat center center fixed;
  background-size: cover;
  overflow: hidden;
`;
